/**
 * Monoline UI — elementar komponentlar (primitives).
 *
 * DESIGN.md 9-bo'limdagi `ui-` sinf shartnomasi va 10-bo'limdagi React API
 * shartnomasining aynan amalga oshirilishi. Bu yerda hech qanday rang qattiq
 * kodlanmaydi — barcha ko'rinish `kit.css` dagi `var(--ui-*)` tokenlariga tayanadi.
 * Inline `style` faqat CSS bila olmaydigan dinamik qiymatlar uchun (o'lcham, kenglik).
 *
 * DESIGN.md 10-bo'lim: BARCHA komponent `className` va `...rest` ni pastga uzatadi
 * va `ref` ni `forwardRef` bilan beradi — istisnosiz.
 *
 * Tashqi bog'liqlik: faqat `react` va `react-icons/hi2`.
 */

import { forwardRef, useId, useRef, useState, Children, cloneElement, isValidElement } from 'react'
import { HiXMark } from 'react-icons/hi2'

/** Sinf nomlarini birlashtiradi: bo'sh/false qiymatlar tashlab yuboriladi. */
export function cx(...a) { return a.filter(Boolean).join(' ') }

/**
 * Matndan emojini olib tashlaydi.
 *
 * Nima uchun kerak: DESIGN.md 8-bo'limi emojini interfeys elementi sifatida
 * taqiqlaydi, lekin emoji bazadagi MA'LUMOT ichida ham keladi — masalan
 * kategoriya nomi "🦠 Virus / Malware" bo'lib saqlangan. Bazani o'zgartirmasdan
 * ekranda uni ko'rsatmaslik kerak: monoxrom tizimda rangli emoji yagona
 * rangli dog' bo'lib qoladi va e'tiborni signal bo'lmagan joyga tortadi.
 *
 * Faqat KO'RSATISH uchun ishlatiladi — saqlanadigan qiymat o'zgarmaydi.
 * Qidiruv va solishtirish asl matn bilan bajarilishi kerak.
 */
// Emoji birikmalarini bir-biriga "yelimlaydigan" ko'rinmas belgilar:
// U+FE0F variant selektor, U+200D ZWJ, U+20E3 kalit belgisi.
// Kodda literal yozilmaydi (ko'rinmaydi va tahrirlashda yo'qoladi), shuning
// uchun RegExp konstruktori bilan quriladi. Alternatsiya, belgilar sinfi emas:
// sinf ichida bu belgilar `no-misleading-character-class` ni ishga soladi.
const EMOJI_GLUE = new RegExp('\\uFE0F|\\u200D|\\u20E3', 'g')

export function stripEmoji(text) {
  if (typeof text !== 'string') return text
  return text
    // Piktogrammalar: emoji, ramziy belgilar, bayroqlar.
    // Faqat Extended_Pictographic — u harf va raqamga tegmaydi.
    .replace(/\p{Extended_Pictographic}/gu, '')
    .replace(EMOJI_GLUE, '')
    // Emoji olingandan keyin qolgan ikki bo'shliq va chetdagi bo'shliqlar
    .replace(/\s{2,}/g, ' ')
    .trim()
}

/** Faqat ishlab chiqish rejimida konsolga ogohlantirish (prodda jim). */
function warnDev(msg) {
  if (import.meta.env?.DEV) console.warn('[ui] ' + msg)
}

// ── Button ──────────────────────────────────────────────────
/**
 * Tugma. DESIGN.md 5-bo'lim: birlamchi (qora fon), ikkilamchi (chegara),
 * xavfli (qizil chegara), sokin (fonsiz). Gradient va ko'tarilish yo'q.
 * `iconOnly` bo'lsa `aria-label` majburiy — bo'lmasa konsolga ogohlantirish chiqadi.
 */
export const Button = forwardRef(function Button(
  { variant = 'secondary', size = 'md', icon: Icon, iconOnly = false,
    loading = false, disabled = false, className, children, type = 'button', ...rest },
  ref
) {
  if (iconOnly && !rest['aria-label'] && !rest.title) {
    warnDev('Ikonka-tugmada aria-label majburiy (DESIGN.md 8-bo\'lim).')
  }
  const off = disabled || loading
  return (
    <button
      ref={ref}
      type={type}
      className={cx(
        'ui-btn',
        `ui-btn--${variant}`,
        size === 'sm' && 'ui-btn--sm',
        iconOnly && 'ui-btn--icon',
        loading && 'is-loading',
        off && 'is-disabled',
        className,
      )}
      disabled={off}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading
        ? <Spinner size={size === 'sm' ? 12 : 14} />
        : Icon ? <Icon aria-hidden="true" /> : null}
      {!iconOnly && children}
    </button>
  )
})
Button.displayName = 'Button'

/**
 * Tugmalar guruhi — yonma-yon turgan tugmalarning chegaralari birlashadi.
 * Faqat vizual o'ram, holatni o'zi boshqarmaydi.
 */
export const ButtonGroup = forwardRef(function ButtonGroup({ className, children, ...rest }, ref) {
  return (
    <div ref={ref} className={cx('ui-btn-group', className)} role="group" {...rest}>
      {children}
    </div>
  )
})
ButtonGroup.displayName = 'ButtonGroup'

// ── Field ───────────────────────────────────────────────────
/**
 * Maydon o'rami: yorliq + boshqaruv + izoh yoki xato matni.
 * `error` berilsa izoh o'rniga xato ko'rsatiladi va `is-invalid` qo'shiladi.
 * `htmlFor` — ichkaridagi input `id` si bilan bog'lash uchun.
 */
export const Field = forwardRef(function Field(
  { label, hint, error, htmlFor, className, children, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cx('ui-field', error && 'is-invalid', className)} {...rest}>
      {label && <label className="ui-field__label" htmlFor={htmlFor}>{label}</label>}
      {children}
      {error
        ? <div className="ui-field__error" role="alert">{error}</div>
        : hint ? <div className="ui-field__hint">{hint}</div> : null}
    </div>
  )
})
Field.displayName = 'Field'

// ── Input ───────────────────────────────────────────────────
/**
 * Bir qatorli matn kiritish. `icon` chapda, `suffix` o'ngda turadi —
 * ikkalasidan biri bo'lsa element `ui-input-wrap` ichiga o'raladi.
 * `invalid` — xato holati (chegara rangi va `aria-invalid`).
 */
export const Input = forwardRef(function Input(
  { size = 'md', icon: Icon, suffix, invalid = false, className, ...rest },
  ref
) {
  const el = (
    <input
      ref={ref}
      className={cx('ui-input', size === 'sm' && 'ui-input--sm', invalid && 'is-invalid', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
  if (!Icon && !suffix) return el
  return (
    <div className="ui-input-wrap">
      {Icon && <span className="ui-input__icon"><Icon aria-hidden="true" /></span>}
      {el}
      {suffix && <span className="ui-input__suffix">{suffix}</span>}
    </div>
  )
})
Input.displayName = 'Input'

// ── Textarea ────────────────────────────────────────────────
/** Ko'p qatorli matn maydoni. `rows` balandlikni belgilaydi. */
export const Textarea = forwardRef(function Textarea(
  { rows = 4, invalid = false, className, ...rest },
  ref
) {
  return (
    <textarea
      ref={ref}
      rows={rows}
      className={cx('ui-textarea', invalid && 'is-invalid', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    />
  )
})
Textarea.displayName = 'Textarea'

// ── Select ──────────────────────────────────────────────────
/**
 * Tanlov ro'yxati — brauzerning tabiiy `<select>` i (mobil va klaviatura uchun eng ishonchlisi).
 * `placeholder` berilsa, bo'sh qiymatli o'chirilgan birinchi variant qo'shiladi.
 */
export const Select = forwardRef(function Select(
  { options = [], placeholder, invalid = false, className, children, ...rest },
  ref
) {
  return (
    <select
      ref={ref}
      className={cx('ui-select', invalid && 'is-invalid', className)}
      aria-invalid={invalid || undefined}
      {...rest}
    >
      {placeholder && <option value="" disabled>{placeholder}</option>}
      {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      {children}
    </select>
  )
})
Select.displayName = 'Select'

// ── Checkbox / Radio ────────────────────────────────────────
/**
 * Belgilash katakchasi. Uslub haqiqiy `<input type="checkbox">` ustiga beriladi —
 * shuning uchun ekran o'quvchi va klaviatura uchun to'liq ochiq qoladi.
 * `ref` boshqaruvning o'ziga (input) beriladi — fokus va validatsiya shu yerda.
 */
export const Checkbox = forwardRef(function Checkbox(
  { checked, onChange, label, disabled = false, className, ...rest },
  ref
) {
  return (
    <label className={cx('ui-check', disabled && 'is-disabled', className)}>
      <input ref={ref} type="checkbox" checked={checked} onChange={onChange}
             disabled={disabled} {...rest} />
      {label && <span>{label}</span>}
    </label>
  )
})
Checkbox.displayName = 'Checkbox'

/**
 * Radio tanlov. Bir guruhdagi elementlar bir xil `name` bilan bog'lanadi —
 * shunda brauzer o'q tugmalari bilan yurishni o'zi ta'minlaydi.
 */
export const Radio = forwardRef(function Radio(
  { name, value, checked, onChange, label, disabled = false, className, ...rest },
  ref
) {
  return (
    <label className={cx('ui-radio', disabled && 'is-disabled', className)}>
      <input ref={ref} type="radio" name={name} value={value} checked={checked}
             onChange={onChange} disabled={disabled} {...rest} />
      {label && <span>{label}</span>}
    </label>
  )
})
Radio.displayName = 'Radio'

// ── Switch ──────────────────────────────────────────────────
/**
 * Yoqish/o'chirish tugmachasi. DESIGN.md 8-bo'lim talabi: `role="switch"` +
 * `aria-checked` + yorliq bilan bog'lanish. Asosda haqiqiy checkbox turgani uchun
 * probel/Enter klaviatura bilan ishlashi tabiiy keladi.
 */
export const Switch = forwardRef(function Switch(
  { checked = false, onChange, label, disabled = false, className, ...rest },
  ref
) {
  const ariaLabel = rest['aria-label'] || (typeof label === 'string' ? label : undefined)
  if (!label && !ariaLabel) warnDev('Switch uchun label yoki aria-label kerak.')
  return (
    <label className={cx('ui-switch', disabled && 'is-disabled', className)}>
      <input
        ref={ref}
        type="checkbox"
        role="switch"
        aria-checked={checked}
        checked={checked}
        onChange={onChange}
        disabled={disabled}
        {...rest}
        aria-label={ariaLabel}
      />
      {label && <span>{label}</span>}
    </label>
  )
})
Switch.displayName = 'Switch'

// ── Segmented ───────────────────────────────────────────────
/**
 * Segmentli almashtirgich — 2–4 ta o'zaro istisno variant uchun (masalan zichlik, davr).
 * Faol element `is-active` oladi; bosilgan holat `aria-pressed` orqali e'lon qilinadi.
 *
 * `items[].count` — filtr yonidagi son ("AI qoralamalari 9"). Filtr ro'yxatni
 * qisqartirsa, foydalanuvchi bosishdan OLDIN natija bor-yo'qligini bilishi
 * kerak: aks holda bo'sh filtrga bosib, keyin orqaga qaytadi. Ko'rinishi
 * `Tabs` dagi hisoblagich bilan bir xil — kitda bitta til.
 */
export const Segmented = forwardRef(function Segmented(
  { items = [], value, onChange, className, ...rest },
  ref
) {
  return (
    <div ref={ref} className={cx('ui-segmented', className)} role="group" {...rest}>
      {items.map(it => {
        const Icon = it.icon
        const active = it.value === value
        return (
          <button
            key={it.value}
            type="button"
            className={cx('ui-segmented__item', active && 'is-active')}
            aria-pressed={active}
            disabled={it.disabled}
            onClick={() => onChange && onChange(it.value)}
          >
            {Icon && <Icon aria-hidden="true" />}
            {it.label}
            {it.count != null && <span className="ui-segmented__count ui-nums">{it.count}</span>}
          </button>
        )
      })}
    </div>
  )
})
Segmented.displayName = 'Segmented'

// ── ChipSelect ──────────────────────────────────────────────
/**
 * Ko'p tanlovli chiplar — Segmented ning ko'p tanlanadigan qarindoshi.
 * Segmented o'zaro ISTISNO variantlar uchun (bittasi yonadi), ChipSelect esa
 * bir vaqtda bir nechtasi yoqiladigan qisqa ro'yxat uchun (masalan bitta
 * yo'riqnoma bir necha turkumga tegishli bo'lishi).
 *
 * `items`: [{ value, label, icon }] — `icon` React komponenti ham, emoji
 * satri ham bo'lishi mumkin (turkum ikonkalari bazada emoji bo'lib turadi).
 * Har chip haqiqiy tugma: `aria-pressed` bilan holatini e'lon qiladi va
 * klaviatura bilan yuriladi. Tanlov FAQAT rang bilan berilmaydi — yoqilganda
 * belgi (✓) ham chiqadi (DESIGN.md 8-bo'lim).
 */
export const ChipSelect = forwardRef(function ChipSelect(
  { items = [], value = [], onChange, empty = 'Variant yo\'q', className, ...rest },
  ref
) {
  const on = new Set(value)
  const toggle = (v) => {
    if (!onChange) return
    onChange(on.has(v) ? value.filter(x => x !== v) : [...value, v])
  }
  return (
    <div ref={ref} className={cx('ui-chips', className)} role="group" {...rest}>
      {items.length === 0 && <span className="ui-chips__empty">{empty}</span>}
      {items.map(it => {
        const Icon = it.icon
        const active = on.has(it.value)
        return (
          <button
            key={it.value}
            type="button"
            className={cx('ui-chips__item', active && 'is-active')}
            aria-pressed={active}
            disabled={it.disabled}
            onClick={() => toggle(it.value)}
          >
            {typeof Icon === 'string' ? <span aria-hidden="true">{Icon}</span>
              : Icon ? <Icon aria-hidden="true" /> : null}
            {it.label}
            {active && <span className="ui-chips__mark" aria-hidden="true">✓</span>}
          </button>
        )
      })}
    </div>
  )
})
ChipSelect.displayName = 'ChipSelect'

// ── Badge / Dot / Tag ───────────────────────────────────────
/**
 * Status yorlig'i. DESIGN.md 4-bo'lim: rang faqat muhimlikni tashiydi,
 * bosqich (Yangi/Qabul/Yopildi) uchun `tone="neutral"` ishlatiladi.
 */
export const Badge = forwardRef(function Badge(
  { tone = 'neutral', icon: Icon, className, children, ...rest },
  ref
) {
  return (
    <span ref={ref} className={cx('ui-badge', `ui-badge--${tone}`, className)} {...rest}>
      {Icon && <Icon aria-hidden="true" />}
      {children}
    </span>
  )
})
Badge.displayName = 'Badge'

const DOT_TEXT = { ok: 'Faol', busy: 'Band', off: 'Oflayn' }

/**
 * Holat nuqtasi. Holat faqat rang bilan berilmasligi kerak (8-bo'lim),
 * shuning uchun nuqtaga o'qiladigan nom (`aria-label` + `title`) biriktiriladi.
 */
export const Dot = forwardRef(function Dot({ tone = 'off', label, className, ...rest }, ref) {
  const text = label || DOT_TEXT[tone] || tone
  return (
    <span
      ref={ref}
      className={cx('ui-dot', `ui-dot--${tone}`, className)}
      role="img"
      aria-label={text}
      title={text}
      {...rest}
    />
  )
})
Dot.displayName = 'Dot'

/**
 * Teg — filtr yoki tanlangan qiymatni ko'rsatadi.
 * `onRemove` berilsa o'ng tomonda o'chirish tugmasi paydo bo'ladi.
 */
export const Tag = forwardRef(function Tag({ onRemove, className, children, ...rest }, ref) {
  return (
    <span ref={ref} className={cx('ui-tag', className)} {...rest}>
      {children}
      {onRemove && (
        <button type="button" className="ui-tag__x" onClick={onRemove}
                aria-label={`O'chirish: ${typeof children === 'string' ? children : ''}`.trim()}>
          <HiXMark aria-hidden="true" />
        </button>
      )}
    </span>
  )
})
Tag.displayName = 'Tag'

// ── Avatar ──────────────────────────────────────────────────
/*
 * Ism bo'yicha barqaror fon tanlash. Mantiq Sidebar.jsx dagi getAvatarColor
 * bilan bir xil (bir xil ism — har doim bir xil natija), lekin palitra
 * monoxrom tizimga moslab neytral yuzalardan olingan: rang ma'lumot tashimaydi,
 * faqat avatarlarni bir-biridan ajratadi. Rangli signal — status nuqtasida.
 */
const AVATAR_TONES = [
  'var(--ui-surface-2)',
  'var(--ui-surface-3)',
  'var(--ui-border)',
  'var(--ui-border-2)',
]

function avatarTone(name = '') {
  let hash = 0
  for (let i = 0; i < name.length; i++) hash = name.charCodeAt(i) + ((hash << 5) - hash)
  return AVATAR_TONES[Math.abs(hash) % AVATAR_TONES.length]
}

/**
 * Foydalanuvchi avatari. `src` bo'lsa rasm, aks holda ismdan hosil qilingan
 * barqaror neytral fon + siluet SVG. `status` berilsa burchakda holat nuqtasi turadi.
 *
 * O'lcham: `size` BERILGANDAgina inline piksel yoziladi (CSS bila olmaydigan
 * dinamik qiymat). Berilmasa inline o'lcham umuman yozilmaydi — shunda
 * `ui-avatar` / `ui-avatar--sm` / `ui-avatar--lg` sinflari kuchga kiradi.
 * Ichkaridagi rasm/SVG bunday holatda o'ramni to'ldiradi (`100%`).
 */
export const Avatar = forwardRef(function Avatar(
  { name = '', src, size, status, className, ...rest },
  ref
) {
  // O'lcham faqat o'ramda: `size` berilsa inline, aks holda sinf (--sm/--lg).
  // Ichkaridagi rasm/SVG ni CSS o'ramga to'ldiradi, shuning uchun bu yerda o'lcham yo'q.
  const box = size != null ? { width: size, height: size } : undefined
  return (
    <span ref={ref} className={cx('ui-avatar', className)} style={box} {...rest}>
      {src
        ? <img src={src} alt={name} />
        : (
          <svg viewBox="0 0 40 40" role="img"
               aria-label={name || 'Foydalanuvchi'}>
            <rect width="40" height="40" fill={avatarTone(name)} />
            <circle cx="20" cy="15" r="7" fill="var(--ui-text-3)" />
            <circle cx="20" cy="38" r="13" fill="var(--ui-text-3)" />
          </svg>
        )}
      {status && <Dot tone={status} className="ui-avatar__status" />}
    </span>
  )
})
Avatar.displayName = 'Avatar'

/**
 * Avatarlar to'plami — ustma-ust siljib turadi. `max` dan ortig'i
 * "+N" hisoblagichiga yig'iladi, shunda qator kengligi sakramaydi.
 */
export const AvatarGroup = forwardRef(function AvatarGroup(
  { max = 3, className, children, ...rest },
  ref
) {
  const list = Children.toArray(children)
  const shown = list.slice(0, max)
  const extra = list.length - shown.length
  return (
    <span ref={ref} className={cx('ui-avatar-group', className)} {...rest}>
      {shown}
      {extra > 0 && (
        <span className="ui-avatar" aria-label={`yana ${extra} ta`}>+{extra}</span>
      )}
    </span>
  )
})
AvatarGroup.displayName = 'AvatarGroup'

// ── Spinner / Progress / Skeleton ───────────────────────────
/** Aylanma yuklanish belgisi. O'lcham piksel sifatida beriladi. */
export const Spinner = forwardRef(function Spinner(
  { size = 16, label = 'Yuklanmoqda', className, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      className={cx('ui-spinner', className)}
      style={{ width: size, height: size }}
      role="status"
      aria-label={label}
      {...rest}
    />
  )
})
Spinner.displayName = 'Spinner'

/**
 * Gorizontal jarayon chizig'i (0..100). To'ldiruvchining kengligi —
 * yagona inline qiymat. `tone` berilsa status rangi qo'llanadi (4-bo'lim).
 */
export const Progress = forwardRef(function Progress(
  { value = 0, tone, label, className, ...rest },
  ref
) {
  const pct = Math.max(0, Math.min(100, Number(value) || 0))
  return (
    <div
      ref={ref}
      className={cx('ui-progress', tone && `ui-progress--${tone}`, className)}
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      {...rest}
    >
      <div className="ui-progress__fill" style={{ width: `${pct}%` }} />
    </div>
  )
})
Progress.displayName = 'Progress'

/**
 * Yuklanish skeleti. Ma'lumot o'rnini egallaydi, ekran o'quvchidan yashiriladi.
 * `width`/`height` — dinamik o'lcham, shuning uchun inline beriladi.
 */
export const Skeleton = forwardRef(function Skeleton(
  { variant = 'text', width, height, className, ...rest },
  ref
) {
  return (
    <span
      ref={ref}
      className={cx('ui-skeleton', `ui-skeleton--${variant}`, className)}
      style={{ width, height }}
      aria-hidden="true"
      {...rest}
    />
  )
})
Skeleton.displayName = 'Skeleton'

// ── Kbd / Code / Divider / Link ─────────────────────────────
/** Klaviatura tugmasi belgisi — qisqartmalarni matn ichida ko'rsatish uchun. */
export const Kbd = forwardRef(function Kbd({ className, children, ...rest }, ref) {
  return <kbd ref={ref} className={cx('ui-kbd', className)} {...rest}>{children}</kbd>
})
Kbd.displayName = 'Kbd'

/** Satr ichidagi kod bo'lagi — monoshirift, yengil fon. */
export const Code = forwardRef(function Code({ className, children, ...rest }, ref) {
  return <code ref={ref} className={cx('ui-code', className)} {...rest}>{children}</code>
})
Code.displayName = 'Code'

/** Ajratgich chiziq. Vertikal ko'rinish uchun `orientation="vertical"`. */
export const Divider = forwardRef(function Divider(
  { orientation = 'horizontal', className, ...rest },
  ref
) {
  return (
    <div
      ref={ref}
      className={cx('ui-divider', orientation === 'vertical' && 'ui-divider--vertical', className)}
      role="separator"
      aria-orientation={orientation}
      {...rest}
    />
  )
})
Divider.displayName = 'Divider'

/**
 * Havola. Monoxrom tizimda havola ham qora (`--ui-text-1`), farqi — tagchiziq.
 * Yangi oynada ochilsa `rel` xavfsizlik uchun avtomatik qo'shiladi.
 */
export const Link = forwardRef(function Link(
  { href, target, rel, className, children, ...rest },
  ref
) {
  const safeRel = target === '_blank' ? (rel || 'noopener noreferrer') : rel
  return (
    <a ref={ref} className={cx('ui-link', className)} href={href} target={target} rel={safeRel} {...rest}>
      {children}
    </a>
  )
})
Link.displayName = 'Link'

// ── Tooltip ─────────────────────────────────────────────────
/**
 * Maslahat oynasi — tashqi kutubxonasiz, faqat holat va CSS.
 * Sichqoncha bilan birga klaviatura fokusida ham ochiladi (8-bo'lim talabi),
 * `Esc` yopadi. Bola element `aria-describedby` orqali matnga bog'lanadi.
 * `ref` — o'ram elementiga (`ui-tooltip-wrap`).
 */
export const Tooltip = forwardRef(function Tooltip(
  { label, placement = 'top', className, children, ...rest },
  ref
) {
  const [open, setOpen] = useState(false)
  const id = useId()
  const timer = useRef(null)

  const show = () => { clearTimeout(timer.current); setOpen(true) }
  const hide = () => { clearTimeout(timer.current); timer.current = setTimeout(() => setOpen(false), 60) }

  // Bolaga aria-describedby biriktiramiz — ekran o'quvchi matnni o'qishi uchun
  const child = isValidElement(children)
    ? cloneElement(children, { 'aria-describedby': open ? id : undefined })
    : children

  if (!label) return children

  return (
    <span
      ref={ref}
      className={cx('ui-tooltip-wrap', className)}
      onMouseEnter={show}
      onMouseLeave={hide}
      onFocus={show}
      onBlur={hide}
      onKeyDown={e => { if (e.key === 'Escape') { clearTimeout(timer.current); setOpen(false) } }}
      {...rest}
    >
      {child}
      {open && (
        <span id={id} role="tooltip" className={cx('ui-tooltip', `ui-tooltip--${placement}`)}>
          {label}
        </span>
      )}
    </span>
  )
})
Tooltip.displayName = 'Tooltip'
